import React, { useState } from "react";
import { supabase } from "../supabase/supabase";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSeasonConfig } from "../hooks/useSeasonConfig";
import PageShell from "../components/PageShell";
import { containsProfanity, PROFANITY_MESSAGE } from "../utils/profanity";
import { TEAM_NAME_MAX, LEAGUE_NAME_MAX } from "../utils/textLimits";

function CreateLeague() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { config: seasonConfig } = useSeasonConfig();
  const currentWeek = seasonConfig?.currentWeek || "Preseason";

  const [leagueName, setLeagueName] = useState("");
  const [teamName, setTeamName] = useState("");
  const [maxManagers, setMaxManagers] = useState(8);
  const [draftOrderType, setDraftOrderType] = useState("random");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!teamName.trim()) {
        setError("Please enter a team name.");
        setLoading(false);
        return;
      }

      // Nothing about the draft is scheduled here. The commissioner starts it
      // from the draft room whenever the league is ready.
      if (containsProfanity(teamName) || containsProfanity(leagueName)) {
        setError(PROFANITY_MESSAGE);
        setLoading(false);
        return;
      }

      const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      const leagueData = {
        name: leagueName,
        created_by: currentUser.id,
        admin_id: currentUser.id,
        max_managers: maxManagers,
        draft_complete: false,
        draft_order_type: draftOrderType,
        scoring_type: "cumulative",
        invite_code: inviteCode,
      };

      // Insert league, then fire drafts + league_members inserts in parallel
      const { data: newLeague, error: leagueError } = await supabase
        .from('leagues').insert(leagueData).select('id, invite_code').single();
      if (leagueError) throw leagueError;

      await Promise.all([
        supabase.from('drafts').insert({ league_id: newLeague.id }),
        supabase.from('league_members').insert({
          league_id: newLeague.id, user_id: currentUser.id, team_name: teamName.trim(),
          points: 0,
        }),
      ]);

      navigate("/home", { state: { message: `League created. Your invite code is: ${newLeague.invite_code}` } });

    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
    border: '1.5px solid var(--color-border)', backgroundColor: 'var(--color-bg-page)', color: 'var(--color-text-strong)',
    outline: 'none', boxSizing: 'border-box',
  };

  const labelStyle = { fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)', display: 'block', marginBottom: 6 };
  const cardStyle = { backgroundColor: 'var(--color-bg-card)', border: '1.5px solid var(--color-bg-subtle)', borderRadius: 16, padding: '20px', marginBottom: 16 };
  const sectionTitle = { fontSize: 15, fontWeight: 700, color: 'var(--color-text-strong)', marginBottom: 16, marginTop: 0 };

  return (
    <PageShell>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--color-text-strong)', margin: 0 }}>Create a League</h1>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginTop: 4 }}>Season week: {currentWeek}</p>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ backgroundColor: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          <p style={{ fontSize: 14, color: 'var(--color-danger-text)', margin: 0 }}>{error}</p>
        </div>
      )}

      <form onSubmit={handleCreateLeague}>
        {/* League settings */}
        <div style={cardStyle}>
          <p style={sectionTitle}>League Settings</p>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>League Name</label>
            <input style={inputStyle} type="text" placeholder="Enter league name" value={leagueName}
              onChange={e => setLeagueName(e.target.value)} maxLength={LEAGUE_NAME_MAX} required />
            <p style={{ fontSize: 12, color: 'var(--color-text-faint)', marginTop: 4 }}>{leagueName.length}/25 characters</p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Number of Managers</label>
            <select style={inputStyle} value={maxManagers} onChange={e => setMaxManagers(Number(e.target.value))} required>
              <option value={8}>8 Managers</option>
              <option value={10}>10 Managers</option>
              <option value={12}>12 Managers</option>
            </select>
          </div>

          <div style={{ marginBottom: draftOrderType !== "manual" ? 12 : 0 }}>
            <label style={labelStyle}>Draft Order</label>
            <select style={inputStyle} value={draftOrderType} onChange={e => setDraftOrderType(e.target.value)}>
              <option value="random">Random Order</option>
              <option value="admin">Commissioner Sets Order</option>
            </select>
          </div>

          {draftOrderType === "random" && (
            <div style={{ backgroundColor: 'var(--color-band-bg)', border: '1px solid var(--color-band-border)', borderRadius: 10, padding: '10px 14px', marginTop: 12 }}>
              <p style={{ fontSize: 13, color: 'var(--color-band-strong)', margin: 0 }}>Draft order will be randomly shuffled when the draft begins.</p>
            </div>
          )}
          {draftOrderType === "admin" && (
            <div style={{ backgroundColor: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-fill)', borderRadius: 10, padding: '10px 14px', marginTop: 12 }}>
              <p style={{ fontSize: 13, color: 'var(--color-warning-text)', margin: 0 }}>You'll be able to arrange the draft order before starting the draft.</p>
            </div>
          )}
        </div>

        {/* How the draft works. Everything here used to be a form field; it
            is now the one explanation of a format with no settings. */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Your Draft</p>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            You invite the managers and fill every place yourself. The app does not
            find managers for you and does not fill empty places.
          </p>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            You start the draft from the draft room when everyone is in. There is
            no scheduled start and no clock on a pick.
          </p>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.5 }}>
            Each manager picks for themselves when their turn comes. You can enter a
            pick for a manager who is not available, and you can give up to two other
            managers the same permission.
          </p>
        </div>

        {/* Team info */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Your Team</p>
          <label style={labelStyle}>Team Name</label>
          <input style={inputStyle} type="text" placeholder="Enter your team name" value={teamName}
            onChange={e => setTeamName(e.target.value)} maxLength={TEAM_NAME_MAX} required />
          <p style={{ fontSize: 12, color: 'var(--color-text-faint)', marginTop: 4 }}>{teamName.length}/20 characters</p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%', padding: '14px', borderRadius: 12, border: 'none',
            backgroundColor: loading ? 'var(--color-bg-subtle)' : 'var(--color-cta)',
            color: loading ? 'var(--color-text-faint)' : 'var(--color-cta-text)',
            fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? "Creating League…" : "Create League"}
        </button>
      </form>
    </PageShell>
  );
}

export default CreateLeague;
